import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDocsFromServer,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { autoria } from '@/leads/autoria';
import { registrarErro, tratarErro } from '@/lib/erros';
import { ouvirVigiado } from '@/lib/listenerVigiado';
import { abertasAFechar, camposDaConclusaoAutomatica, sufixoDaConclusao } from '@/leads/conclusaoAutomatica';

const colecaoTarefas = (clinicaId, leadId) =>
  collection(db, 'clinicas', clinicaId, 'leads', leadId, 'tarefas');

const refTarefa = (clinicaId, leadId, tarefaId) =>
  doc(db, 'clinicas', clinicaId, 'leads', leadId, 'tarefas', tarefaId);

const colecaoInteracoes = (clinicaId, leadId) =>
  collection(db, 'clinicas', clinicaId, 'leads', leadId, 'interacoes');
const refLead = (clinicaId, leadId) => doc(db, 'clinicas', clinicaId, 'leads', leadId);

export const TIPOS_TAREFA = [
  { id: 'ligacao', label: 'Ligar' },
  { id: 'whatsapp', label: 'Mandar WhatsApp' },
  { id: 'retorno', label: 'Retornar contato' },
  { id: 'outro', label: 'Outro' },
];

/**
 * Fila de follow-ups da clínica (PLANO 4.4 — tela "Próximos Contatos").
 *
 * Consulta por grupo de coleção: as tarefas vivem sob cada lead, mas a fila do
 * dia precisa vê-las juntas. O filtro por clinicaId não é só conveniência — é
 * o que as security rules exigem para autorizar a leitura.
 *
 * ★ PC-4 — com `aoDesatualizar` (5º parâmetro), a MESMA consulta passa pelo envelope da WU-2
 * (`ouvirVigiado`: reinscreve sozinho, confere a 1ª emissão do cache com o servidor) e
 * `aoReceber` ganha um 2º argumento, `{ doServidor }` — ★ é por ele que o filtro "Sem próximo
 * contato" do Funil e da Lista sabe se pode confiar na lista. ⚠ Sem o 5º parâmetro o caminho é
 * o de sempre (a tela Próximos contatos não muda).
 */
export function ouvirProximosContatos(clinicaId, { responsavelId } = {}, aoReceber, aoFalhar, aoDesatualizar) {
  const restricoes = [
    where('clinicaId', '==', clinicaId),
    where('concluida', '==', false),
  ];
  if (responsavelId) restricoes.push(where('responsavelId', '==', responsavelId));

  const q = query(collectionGroup(db, 'tarefas'), ...restricoes, orderBy('dataHora', 'asc'));
  const lista = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (aoDesatualizar) {
    return ouvirVigiado({
      assunto: 'os próximos contatos',
      assinar: (aoSnap, aoErro) => onSnapshot(
        q,
        { includeMetadataChanges: true },
        (snap) => aoSnap({ lista: lista(snap), doServidor: !snap.metadata.fromCache }, snap.metadata.fromCache),
        aoErro,
      ),
      doServidor: async () => ({ lista: lista(await getDocsFromServer(q)), doServidor: true }),
      aoReceber: (v) => aoReceber(v.lista, { doServidor: v.doServidor }),
      aoFalhar,
      aoDesatualizar,
    });
  }
  return onSnapshot(
    q,
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('os próximos contatos', aoFalhar),
  );
}

export function ouvirTarefasDoLead(clinicaId, leadId, aoReceber, aoFalhar) {
  const q = query(colecaoTarefas(clinicaId, leadId), orderBy('dataHora', 'asc'));
  return onSnapshot(
    q,
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    tratarErro('as tarefas do lead', aoFalhar),
  );
}

const formatarDataHora = (d) => d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Monta o documento de uma tarefa — PURO (sem escrita), reusado por
 * `criarTarefa` e pelo retorno automático do comparecimento
 * (`agendamentosService.registrarComparecimento`, V4), que precisa gravar a
 * tarefa DENTRO do mesmo lote do agendamento.
 *
 * Lança em data inválida. `agora` é injetável para os testes puros; em produção
 * é `serverTimestamp()`.
 *
 * `responsavelId`: o pedido → o SDR do lead → quem está criando. Para o retorno
 * pós-avaliação quem chama passa `negociadorId ?? sdrId`.
 */
/**
 * ★ PC-6 D7 — o responsável GRAVADO na criação: o pedido → o SDR do lead → quem criou; id e nome do
 * MESMO ramo. ⚠ Não é a herança da LEITURA (`fila.js:responsavelDaTarefa`, VV-c1) — é a escrita.
 */
export function responsavelAoCriar(dados = {}, lead = {}, autor = null) {
  if (dados.responsavelId) return { responsavelId: dados.responsavelId, responsavelNome: dados.responsavelNome ?? '' };
  if (lead?.sdrId) return { responsavelId: lead.sdrId, responsavelNome: lead.sdrNome ?? '' };
  return { responsavelId: autor?.id ?? '', responsavelNome: autor?.nome ?? '' };
}

export function montarTarefa(clinicaId, lead, dados, autor, { agora = serverTimestamp() } = {}) {
  const dataHora = dados.dataHora instanceof Date ? dados.dataHora : new Date(dados.dataHora);
  if (Number.isNaN(dataHora.getTime())) {
    throw new Error('Informe uma data e hora válidas para o contato.');
  }
  if (!clinicaId) throw new Error('Tarefa sem clínica.');

  return {
    // ★ VV-c2: título padrão "Próximo contato" (era 'Follow-up'; o já gravado não é reescrito).
    titulo: String(dados.titulo ?? '').trim() || 'Próximo contato',
    tipo: dados.tipo ?? 'ligacao',
    dataHora,
    concluida: false,
    // De onde a tarefa veio: 'manual' (painel) ou 'comparecimento' (automática).
    origem: dados.origem ?? 'manual',
    // Desnormalizados para que a fila do dia se desenhe sem ler cada lead.
    leadId: lead.id,
    leadNome: lead.nome ?? '',
    leadTelefone: lead.telefone ?? '',
    leadEtapa: dados.leadEtapa ?? lead.etapa ?? '',
    // ★★ PC-6 D7: `||`, não `??` — lead SEM SDR tem `sdrId: ''`, que o `??` aceitava, e a tarefa
    // nascia sem responsável (fora da fila de todo mundo). Agora fica com quem a criou. ⚠ E o NOME
    // sai do MESMO ramo que o id: com dois `??` separados, id e nome podiam vir de pessoas diferentes.
    ...responsavelAoCriar(dados, lead, autor),
    clinicaId,
    criadoPorId: autor?.id ?? 'sistema',
    criadoPorNome: autor?.nome ?? 'Sistema',
    criadoEm: agora,
  };
}

/** Texto da interação `tarefa` que acompanha a criação. */
export const textoInteracaoTarefa = (tarefa) =>
  `Próximo contato agendado: ${tarefa.titulo} — ${formatarDataHora(tarefa.dataHora)}.`;

export async function criarTarefa(clinicaId, lead, dados, autor) {
  const tarefa = montarTarefa(clinicaId, lead, dados, autor);

  /**
   * ★★ WO — O LEAD É CARIMBADO JUNTO, e isto é conserto de defeito medido.
   *
   * Até aqui, criar "próximo contato" gravava a tarefa e a interação e **não tocava o lead**.
   * O lead sumia das listas de "tocados recentemente" e da ordenação do funil — e um dia em
   * que a SDR só agendou retorno ficava invisível. ⚠ Guarda de fonte em
   * `wo.autoria.guardas.test.js` (T8) cobra que os CINCO caminhos carimbem.
   *
   * ★ E as três escritas passam a ir no MESMO lote: ou tudo acontece, ou nada.
   *
   * ★★ PC-2 (#1) — o novo contato ENCERRA as abertas do MESMO responsável (decisão 3).
   * A leitura é ANTES do lote e do servidor; o id novo vai em `excetoIds` (2ª barreira).
   */
  const ref = doc(colecaoTarefas(clinicaId, lead.id));
  const { abertas } = await lerAbertasDoLead(clinicaId, lead.id);
  const aFechar = abertasAFechar(abertas, {
    motivo: 'novo_contato',
    responsavelNovo: tarefa.responsavelId,
    sdrDoLead: lead.sdrId,
    excetoIds: [ref.id],
  });
  const montar = (fechar) => {
    const lote = writeBatch(db);
    lote.set(ref, tarefa);
    lote.set(doc(colecaoInteracoes(clinicaId, lead.id)), {
      tipo: 'tarefa',
      texto: textoInteracaoTarefa(tarefa) + sufixoDaConclusao(fechar.length),
      ...autoria(autor, { clinicaId, lead, carimbo: serverTimestamp }),
    });
    lote.update(refLead(clinicaId, lead.id), { atualizadoEm: serverTimestamp() });
    concluirAbertasNoLote(lote, clinicaId, lead.id, fechar, 'novo_contato', autor);
    return lote;
  };
  await comitarComConclusoes(montar, aFechar);
  return ref.id;
}

/**
 * ★★ PC-2 — as tarefas ABERTAS do lead, lidas DO SERVIDOR.
 *
 * ⚠⚠ `getDocsFromServer`, nunca `getDocs` (guarda G4): o `db` tem cache persistente, e
 * `getDocs` sem rede cai CALADO no cache — um cache sem uma aberta fecharia parte delas; um
 * cache com uma tarefa que outra pessoa já concluiu sobrescreveria a autoria humana.
 *
 * ★ Em QUALQUER falha o gesto segue e não fecha nada: devolve `{ abertas: [], falhou: true }`
 * e registra só o código — ⚠ sem id de lead (WY: o id pode carregar dado do paciente).
 */
export async function lerAbertasDoLead(clinicaId, leadId) {
  try {
    const snap = await getDocsFromServer(
      query(colecaoTarefas(clinicaId, leadId), where('concluida', '==', false)),
    );
    return { abertas: snap.docs.map((d) => ({ id: d.id, ...d.data() })), falhou: false };
  } catch (falha) {
    registrarErro({
      origem: 'service',
      codigo: 'pc2:abertas-sem-servidor',
      mensagem: `leitura das tarefas abertas falhou: ${falha?.code ?? 'sem-codigo'}`,
    });
    return { abertas: [], falhou: true };
  }
}

/** ★ PC-2 — um `update` por tarefa encerrada, com a marca do sistema, DENTRO do lote do gesto. */
export function concluirAbertasNoLote(lote, clinicaId, leadId, aFechar, motivo, autor) {
  for (const t of aFechar ?? []) {
    lote.update(
      refTarefa(clinicaId, leadId, t.id),
      camposDaConclusaoAutomatica(motivo, autor, { carimbo: serverTimestamp }),
    );
  }
  return (aFechar ?? []).length;
}

/**
 * ★★ PC-2 — comita o lote do gesto; se uma tarefa a encerrar SUMIU entre a leitura e o
 * commit (outra pessoa a excluiu), repete UMA vez sem conclusões.
 *
 * ★ Seguro porque lote que falha não grava nada (é atômico), e porque `montar` reusa o
 * `doc()` da tarefa/agendamento gerado FORA dela — a repetição devolve o mesmo id.
 * ⚠ O gesto da pessoa nunca é perdido por causa da limpeza; o contrário seria inverter a
 * prioridade.
 *
 * ★★★ MEDIDO no E13 (`docs/pc2-testes.md`): o lote com `update` em tarefa EXCLUÍDA volta
 * **`permission-denied`**, não `not-found` — a regra de `update` de `tarefas` lê
 * `resource.data.clinicaId`, e o documento não existe. ⚠ O plano supunha `not-found`; com só
 * ele, a repetição nunca aconteceria e o gesto falharia (na baixa, com a frase do negociador).
 * ★ Por isso as duas recusas repetem. Se a repetição SEM conclusões também falhar, quem foi
 * recusado é o GESTO — a falha dela sobe como sempre subiu, sem registro da PC-2.
 */
const RECUSAS_QUE_REPETEM = ['not-found', 'permission-denied'];

export async function comitarComConclusoes(montar, aFechar) {
  try {
    await montar(aFechar).commit();
    return (aFechar ?? []).length;
  } catch (falha) {
    if (!(aFechar ?? []).length || !RECUSAS_QUE_REPETEM.includes(falha?.code)) throw falha;
    await montar([]).commit();
    registrarErro({
      origem: 'service',
      codigo: 'pc2:conclusao-repetida',
      mensagem: `lote com tarefa a encerrar recusado (${falha.code}); repetido sem conclusões`,
    });
    return 0;
  }
}

/**
 * Conclui a tarefa e deixa o registro na timeline do lead.
 *
 * V4: também carimba `ultimoContatoEm` no lead, no MESMO lote. É o que alimenta
 * o "sem retorno há X dias" do funil sem consulta extra por card.
 */
export async function concluirTarefa(clinicaId, tarefa, autor) {
  const lote = writeBatch(db);
  lote.update(refTarefa(clinicaId, tarefa.leadId, tarefa.id), {
    concluida: true,
    concluidaEm: serverTimestamp(),
    concluidaPorId: autor?.id ?? 'sistema',
    concluidaPorNome: autor?.nome ?? 'Sistema',
  });
  lote.update(refLead(clinicaId, tarefa.leadId), {
    ultimoContatoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });
  lote.set(doc(colecaoInteracoes(clinicaId, tarefa.leadId)), {
    tipo: 'tarefa',
    texto: `Próximo contato concluído: ${tarefa.titulo}.`,
    ...autoria(autor, { clinicaId, carimbo: serverTimestamp }),
  });
  await lote.commit();
}

export async function reagendarTarefa(clinicaId, tarefa, novaData, autor) {
  const data = novaData instanceof Date ? novaData : new Date(novaData);
  if (Number.isNaN(data.getTime())) throw new Error('Data inválida para reagendar.');

  /**
   * ★★ WO — reagendar também CARIMBA O LEAD, e vai num lote só.
   *
   * ⚠ Antes eram duas escritas soltas (`updateDoc` + `addDoc`) e o lead não era tocado.
   * Reagendar é metade da definição de follow-up do negociador (D2) — sem o carimbo, o
   * trabalho dele some das listas. Guarda T8.
   *
   * ⚠ O documento da TAREFA continua guardando só o valor atual de `dataHora`: quem
   * reagendou e quando fica na timeline, não nela. É limite declarado, não esquecimento.
   */
  const lote = writeBatch(db);
  lote.update(refTarefa(clinicaId, tarefa.leadId, tarefa.id), { dataHora: data });
  lote.set(doc(colecaoInteracoes(clinicaId, tarefa.leadId)), {
    tipo: 'tarefa',
    texto: `Próximo contato reagendado para ${data.toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    })}.`,
    ...autoria(autor, { clinicaId, carimbo: serverTimestamp }),
  });
  lote.update(refLead(clinicaId, tarefa.leadId), { atualizadoEm: serverTimestamp() });
  await lote.commit();
}

export async function excluirTarefa(clinicaId, tarefa) {
  await deleteDoc(refTarefa(clinicaId, tarefa.leadId, tarefa.id));
}
